import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Auth.css';
import { CalendarIcon, MicrosoftIcon, PassIcon, ShieldIcon } from '../components/Icons';

function ManualLoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onLogin(email, password);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="auth-form">
      <div className="field">
        <label>Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label>Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in (email)'}
        </button>
      </div>
    </form>
  );
}

function AoRegisterForm({ onRegister }) {
  const [name, setName] = useState('AO User');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [department, setDepartment] = useState('Administration');
  const [employeeId, setEmployeeId] = useState('AO001');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onRegister({ name, email, password, department, employeeId, role: 'ao' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="auth-form">
      <div className="field">
        <label>Full name</label>
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label>Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label>Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <div className="grid-2">
        <div className="field">
          <label>Department</label>
          <input value={department} onChange={(e) => setDepartment(e.target.value)} />
        </div>
        <div className="field">
          <label>Employee ID</label>
          <input value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Creating…' : 'Create AO account'}
        </button>
      </div>
    </form>
  );
}

export default function AuthPage() {
  const { loginWithMicrosoft, login, register, loading } = useAuth();
  const navigate = useNavigate();
  const [showManual, setShowManual] = React.useState(false);
  const [showRegister, setShowRegister] = React.useState(false);

  const goHomeForRole = (faculty) => {
    navigate(faculty?.role === 'ao' ? '/ao' : '/dashboard', { replace: true });
  };
  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-inner">
          <div className="auth-brand">
            <div className="brand-chip">Campus visitor management</div>
            <h1 className="brand-name">VisitorPass</h1>
            <p className="brand-tagline">A clean, mobile-friendly portal for issuing passes, verifying entry, and keeping the gate flow fast.</p>
          </div>

          <div className="auth-stat-row">
            <div className="auth-stat">
              <div className="auth-stat-value">01</div>
              <div className="auth-stat-label">Login path</div>
            </div>
            <div className="auth-stat">
              <div className="auth-stat-value">24/7</div>
              <div className="auth-stat-label">Gate ready</div>
            </div>
            <div className="auth-stat">
              <div className="auth-stat-value">QR</div>
              <div className="auth-stat-label">Quick verify</div>
            </div>
          </div>

          <div className="auth-features">
            <div className="feature-item"><span className="feature-icon"><MicrosoftIcon /></span><div><strong>Microsoft sign-in</strong><p>Secure access with your institution account.</p></div></div>
            <div className="feature-item"><span className="feature-icon"><CalendarIcon /></span><div><strong>Schedule events</strong><p>Create structured events with clear time and venue details.</p></div></div>
            <div className="feature-item"><span className="feature-icon"><PassIcon /></span><div><strong>Issue passes</strong><p>Generate pass codes and QR details for every visitor.</p></div></div>
            <div className="feature-item"><span className="feature-icon"><ShieldIcon /></span><div><strong>Gate security</strong><p>Verify entries quickly from a dedicated security view.</p></div></div>
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-card fade-in">
          <div className="auth-panel">
            <div className="auth-heading">
              <h2>Sign in</h2>
              <p>Use Microsoft to open the dashboard and manage visitor flow.</p>
            </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                loginWithMicrosoft();
              }}
              className="auth-form"
            >
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? <><span className="spinner" /> Redirecting...</> : <><MicrosoftIcon /> Login with Microsoft</>}
              </button>
            </form>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button type="button" className="btn-ghost" onClick={() => setShowManual((s) => !s)}>
                {showManual ? 'Hide manual login' : 'Manual AO login'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setShowRegister((s) => !s)}>
                {showRegister ? 'Hide AO register' : 'Create AO account'}
              </button>
            </div>

            {showManual && (
              <div style={{ marginTop: '0.75rem' }}>
                <ManualLoginForm onLogin={async (email, password) => {
                  try {
                    const res = await login(email, password);
                    goHomeForRole(res?.faculty);
                  } catch (err) {
                    // handled by login caller
                  }
                }} />
              </div>
            )}

            {showRegister && (
              <div style={{ marginTop: '0.75rem' }}>
                <AoRegisterForm onRegister={async (data) => {
                  try {
                    const res = await register(data);
                    goHomeForRole(res?.faculty);
                  } catch (err) {
                    // registration errors handled by register
                  }
                }} />
              </div>
            )}
          </div>
            <div className="auth-note">Production redirect and client URLs must match your Vercel deployment and Azure app registration.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
